import { SEASON_PRESETS, type SeasonRegion } from '@sea-snag/shared';
import { Progress } from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';

import { analyticsApi } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { formatSeasonRange, presetSeasonLabel } from '../../lib/season-format';
import { seasonStatus } from '../../lib/season-status';
import { SeasonSettings } from './season-settings';

interface SeasonOverviewStripProps {
  orgPid: string;
  year: string;
  region: SeasonRegion;
}

/**
 * The organisation's season for a year: its dates, how far through it we are,
 * and, for owners and admins, where to change it. Occupancy and the dashboard's
 * Season Progress widget show this same strip, so they always agree.
 */
export function SeasonOverviewStrip({ orgPid, year, region }: SeasonOverviewStripProps) {
  const { data: season } = useQuery({
    queryKey: queryKeys.analytics.seasonOverview(orgPid, year),
    queryFn: () => analyticsApi.seasonOverview({ year }),
    enabled: !!orgPid,
  });
  const status = season ? seasonStatus(season.progress, region === 'year_round') : null;
  const regionLabel = SEASON_PRESETS[region].label;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
        <span className="font-medium">
          Season overview — {regionLabel}
          {season
            ? ` · ${formatSeasonRange(season.startDate, season.endDate)}`
            : region !== 'year_round'
              ? ` · ${presetSeasonLabel(region)}`
              : ''}
        </span>
        {status && <span className="text-muted-foreground">{status.text}</span>}
      </div>
      <Progress value={status?.pct ?? 0} />
      <SeasonSettings orgPid={orgPid} year={year} season={season} regionLabel={regionLabel} />
    </div>
  );
}
